import axiosInstance from "@/axiosConfig";
import { organizationSiteInput } from "@/features/organization-settings/subFeatures/organization-sites/schemas/organizationSite.schema";
import type {
  OrganizationSitesResponse,
  OrganizationSite,
  OrganizationPlanResponse,
} from "@/shared/types/common";
import type { CseInformations } from "@/shared/types/organization.types";
import { getApiUrl } from "@/shared/utils/api";
import { buildUrlWithParams } from "@/shared/utils/query-params";

/** Large limit to fetch all records in a single request */
const FETCH_ALL_LIMIT = 9999;

type SiteEmployeeSummary = {
  id: string
  first_name: string
  last_name: string
  email: string
};

type SiteEmployeesResponse = {
  employees: SiteEmployeeSummary[]
  total: number
};

type TransferEmployeesResponse = {
  success: true
  transferred_count: number
};

// Service class for all organization API calls
export class OrganizationService {
  /**
   * Fetch organization by ID
   * @param organizationId - ID of the organization
   * @returns Promise<CseInformations>
   * @method GET
   */
  static async getOrganization(
    organizationId: string,
  ): Promise<CseInformations> {
    const response = await axiosInstance.get<CseInformations>(
      getApiUrl(`/organizations/${organizationId}`),
    );
    return response.data;
  }

  /**
   * Update organization by ID
   * @param organizationId - ID of the organization
   * @param payload - Organization data to update
   * @param isPartialUpdate - Whether to use PATCH (true) or PUT (false)
   * @returns Promise<CseInformations>
   * @method PUT/PATCH
   */
  static async updateOrganization(
    organizationId: string,
    payload: Partial<CseInformations["organization"]>,
    isPartialUpdate: boolean = false,
  ): Promise<CseInformations> {
    const response = isPartialUpdate
      ? await axiosInstance.patch<{ organization: CseInformations["organization"] }>(getApiUrl(`/organizations/${organizationId}`), payload)
      : await axiosInstance.put<{ organization: CseInformations["organization"] }>(getApiUrl(`/organizations/${organizationId}`), payload);
    // L'API retourne {organization: {...}} mais nous devons retourner la structure CseInformations
    return { organization: response.data.organization };
  }

  /**
   * Fetch organization sites
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 50)
   * @returns Promise<OrganizationSitesResponse>
   * @method GET
   */
  static async getOrganizationSites(
    page: number = 1,
    limit: number = FETCH_ALL_LIMIT,
  ): Promise<OrganizationSitesResponse> {
    const url = getApiUrl(
      buildUrlWithParams("/organization_sites", { page, limit, fields: "id,name" }),
    );

    const response = await axiosInstance.get<OrganizationSitesResponse>(url);
    return response.data;
  }

  /**
   * Fetch organization sites
   * @param queryParams - URLSearchParams
   * @returns Promise<OrganizationSitesResponse>
   * @method GET
   */
  static async getOrganizationSitesWithParams(queryParams: URLSearchParams): Promise<OrganizationSitesResponse> {
    const response = await axiosInstance.get<OrganizationSitesResponse>(
      getApiUrl(`/organization_sites?${queryParams.toString()}`),
    );
    return response.data;
  }

  /**
   * Create organization site
   * @param siteData - Site data to create
   * @returns Promise<{ organization_site: OrganizationSite }>
   * @method POST
   */
  static async createOrganizationSite(
    siteData: organizationSiteInput,
  ): Promise<{ organization_site: OrganizationSite }> {
    const response = await axiosInstance.post<{ organization_site: OrganizationSite }>(getApiUrl("/organization_sites"), { organization_site: siteData });
    return response.data;
  }

  /**
   * Update organization site
   * @param siteId - ID of the site to update
   * @param siteData - Site data to update
   * @param confirmed - ADM-856: acknowledges the impact on dynamic groups reported
   *   by a prior 409; never send this upfront, the server recompute is the guard
   * @returns Promise<{ organization_site: OrganizationSite }>
   * @method PUT
   */
  static async updateOrganizationSite(
    siteId: string,
    siteData: organizationSiteInput,
    confirmed = false,
  ): Promise<{ organization_site: OrganizationSite }> {
    const payload = confirmed
      ? { organization_site: siteData, confirmed: true }
      : { organization_site: siteData };
    const response = await axiosInstance.put<{ organization_site: OrganizationSite }>(getApiUrl(`/organization_sites/${siteId}`), payload);
    return response.data;
  }

  /**
   * Delete organization site
   * @param siteId - ID of the site to delete
   * @param confirmed - ADM-856: see updateOrganizationSite
   * @returns Promise<void>
   * @method DELETE
   */
  static async deleteOrganizationSite(siteId: string, confirmed = false): Promise<void> {
    await axiosInstance.delete(
      getApiUrl(`/organization_sites/${siteId}`),
      confirmed ? { params: { confirmed: true } } : undefined,
    );
  }

  /**
   * Fetch the list of currently-employed employees attached to a site.
   * Used by the delete-site modal to warn the user and offer a transfer.
   * @method GET
   */
  static async getCurrentlyEmployedAtSite(siteId: string): Promise<SiteEmployeesResponse> {
    const response = await axiosInstance.get<SiteEmployeesResponse>(
      getApiUrl(`/organization_sites/${siteId}/currently_employed_employees`),
    );
    return response.data;
  }

  /**
   * Bulk-transfer all currently-employed employees of a site to another site.
   * Used by the delete-site modal before destruction.
   * @method POST
   */
  static async transferCurrentlyEmployedAtSite(
    siteId: string,
    targetSiteId: string,
  ): Promise<TransferEmployeesResponse> {
    const response = await axiosInstance.post<TransferEmployeesResponse>(
      getApiUrl(`/organization_sites/${siteId}/transfer_currently_employed_employees`),
      { target_site_id: targetSiteId },
    );
    return response.data;
  }

  /**
   * Fetch organization features and plan information
   * @param organizationId - ID of the organization (format: ORG_XXXXX)
   * @returns Promise<OrganizationPlanResponse>
   * @method GET
   */
  static async getOrganizationPlan(
    organizationId: string,
  ): Promise<OrganizationPlanResponse> {
    const response = await axiosInstance.get<OrganizationPlanResponse>(
      getApiUrl(`/organizations/${organizationId}/plan`),
    );
    return response.data;
  }

  /**
   * Request upsell contact for an organization
   * @param organizationId - ID of the organization
   * @returns Promise<void>
   * @method POST
   */
  static async requestUpsellContact(
    organizationId: string,
  ): Promise<void> {
    await axiosInstance.post(
      getApiUrl(`/organizations/${organizationId}/request_upsell_contact`),
    );
  }
}

// Export individual functions for easier migration/testing
export const organizationApi = {
  getOrganization: OrganizationService.getOrganization,
  updateOrganization: OrganizationService.updateOrganization,
  getOrganizationSites: (page?: number, limit?: number) =>
    OrganizationService.getOrganizationSites(page, limit),
  createOrganizationSite: OrganizationService.createOrganizationSite,
  updateOrganizationSite: OrganizationService.updateOrganizationSite,
  deleteOrganizationSite: OrganizationService.deleteOrganizationSite,
  getOrganizationPlan: OrganizationService.getOrganizationPlan,
  requestUpsellContact: OrganizationService.requestUpsellContact,
};

// Legacy export for backward compatibility
export const getOrganization = OrganizationService.getOrganization;

// Default export for the service
export default OrganizationService;
